import {
  AppLocale,
  DEFAULT_LOCALE,
  getLocaleConfig,
  getLocaleFromPathPrefix,
  isLocalePathPrefix,
  LOCALES,
} from "./localesConfig";

export type AudienceSlug = "cities" | "local-governments" | "hoas";

const EN_AUDIENCE: Record<AudienceSlug, string> = {
  cities: "cities",
  "local-governments": "local-governments",
  hoas: "hoas",
};

const ES_AUDIENCE: Record<AudienceSlug, string> = {
  cities: "ayuntamientos",
  "local-governments": "gobiernos-locales",
  hoas: "comunidades",
};

const ES_AUDIENCE_REVERSE: Record<string, AudienceSlug> = {
  ayuntamientos: "cities",
  "gobiernos-locales": "local-governments",
  comunidades: "hoas",
};

const stripLocalePrefixFromPath = (
  fullPath: string,
  locale: AppLocale
): string => {
  const prefix = getLocaleConfig(locale).pathPrefix;
  if (!prefix) return fullPath;
  if (fullPath === `/${prefix}`) return "/";
  if (fullPath.startsWith(`/${prefix}/`)) {
    return fullPath.slice(prefix.length + 1);
  }
  return fullPath;
};

/**
 * Prefix a site-relative path with the active locale (if any).
 * Pass paths without a leading locale segment, e.g. `/pricing` or `/for/cities`.
 */
export const localizedPath = (
  path: string,
  locale: AppLocale = DEFAULT_LOCALE
): string => {
  const config = getLocaleConfig(locale);
  const normalized = path.startsWith("/") ? path : `/${path}`;

  if (!config.pathPrefix) {
    return normalized === "" ? "/" : normalized;
  }

  if (normalized === "/") {
    return `/${config.pathPrefix}`;
  }

  return `/${config.pathPrefix}${normalized}`;
};

/** Map logical audience key to the URL segment used for a locale. */
export const audiencePathSegment = (
  audience: AudienceSlug,
  locale: AppLocale
): string => {
  const config = getLocaleConfig(locale);
  if (config.audiencePathStyle === "para") {
    return ES_AUDIENCE[audience];
  }
  return EN_AUDIENCE[audience];
};

/** Unprefixed audience landing path for a locale. */
export const audienceRelativePath = (
  audience: AudienceSlug,
  locale: AppLocale
): string => {
  const config = getLocaleConfig(locale);
  const segment = audiencePathSegment(audience, locale);
  return config.audiencePathStyle === "para"
    ? `/para/${segment}`
    : `/for/${segment}`;
};

/** Audience landing path for a locale, e.g. `/es-es/para/ayuntamientos`. */
export const audiencePath = (
  audience: AudienceSlug,
  locale: AppLocale = DEFAULT_LOCALE
): string => localizedPath(audienceRelativePath(audience, locale), locale);

export const pricingRelativePath = (locale: AppLocale): string => {
  const config = getLocaleConfig(locale);
  return config.audiencePathStyle === "para" ? "/precios" : "/pricing";
};

export const pricingPath = (locale: AppLocale = DEFAULT_LOCALE): string =>
  localizedPath(pricingRelativePath(locale), locale);

export const blogPath = (locale: AppLocale = DEFAULT_LOCALE): string =>
  localizedPath("/blog", locale);

export const termsPath = (locale: AppLocale = DEFAULT_LOCALE): string =>
  localizedPath("/terms-privacy", locale);

export const marketsPath = (
  countrySlug: string,
  locale: AppLocale = DEFAULT_LOCALE,
  citySlug?: string
): string => {
  const base = citySlug
    ? `/markets/${countrySlug}/${citySlug}`
    : `/markets/${countrySlug}`;
  return localizedPath(base, locale);
};

export const homePath = (locale: AppLocale = DEFAULT_LOCALE): string =>
  localizedPath("/", locale);

/**
 * Resolve locale + strip prefix from a pathname.
 */
export const parseLocaleFromPathname = (
  pathname: string
): { locale: AppLocale; pathnameWithoutLocale: string } => {
  const parts = pathname.split("/").filter(Boolean);
  const first = parts[0];
  if (isLocalePathPrefix(first)) {
    const locale = getLocaleFromPathPrefix(first) || DEFAULT_LOCALE;
    const rest = parts.slice(1).join("/");
    return {
      locale,
      pathnameWithoutLocale: rest ? `/${rest}` : "/",
    };
  }
  return { locale: DEFAULT_LOCALE, pathnameWithoutLocale: pathname || "/" };
};

/**
 * Detect audience key from an unprefixed path like `/for/cities` or `/para/ayuntamientos`.
 */
export const audienceFromPath = (
  pathnameWithoutLocale: string
): AudienceSlug | null => {
  const parts = pathnameWithoutLocale.split("/").filter(Boolean);
  if (
    (parts[0] === "for" || parts[0] === "para") &&
    parts[1]
  ) {
    return audienceFromSegment(parts[1]);
  }
  return null;
};

/** Resolve audience key from a path segment under /for or /para. */
export const audienceFromSegment = (
  segment: string | undefined
): AudienceSlug | null => {
  if (!segment) return null;
  const enKey = (Object.keys(EN_AUDIENCE) as AudienceSlug[]).find(
    (key) => EN_AUDIENCE[key] === segment
  );
  if (enKey) return enKey;
  if (ES_AUDIENCE_REVERSE[segment]) {
    return ES_AUDIENCE_REVERSE[segment];
  }
  return null;
};

/**
 * Logical unprefixed path for a locale (audience/pricing remapped).
 */
export const logicalRelativePath = (
  pathnameWithoutLocale: string,
  targetLocale: AppLocale
): string => {
  const audience = audienceFromPath(pathnameWithoutLocale);
  if (audience) {
    return audienceRelativePath(audience, targetLocale);
  }

  const parts = pathnameWithoutLocale.split("/").filter(Boolean);
  if (parts[0] === "pricing" || parts[0] === "precios") {
    return pricingRelativePath(targetLocale);
  }

  return pathnameWithoutLocale === "" ? "/" : pathnameWithoutLocale;
};

/**
 * Swap the locale prefix on the current path while preserving the logical page.
 */
export const switchLocalePath = (
  pathname: string,
  targetLocale: AppLocale
): string => {
  const { pathnameWithoutLocale } = parseLocaleFromPathname(pathname);
  const remapped = logicalRelativePath(pathnameWithoutLocale, targetLocale);
  return localizedPath(remapped, targetLocale);
};

export const allLocaleConfigs = () => Object.values(LOCALES);

export { stripLocalePrefixFromPath, EN_AUDIENCE, ES_AUDIENCE };
